import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Spinner, Modal } from 'react-bootstrap';
import { BsTrash, BsImage, BsClipboard } from 'react-icons/bs';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import mediaService from '../api/mediaService';
import { useSelector } from 'react-redux';

const FOLDER_OPTIONS = [
  { value: 'posters', label: 'Affiches' },
  { value: 'representations', label: 'Représentations' },
  { value: 'channels', label: 'Canaux' },
  { value: 'pages', label: 'Pages' },
  { value: 'general', label: 'Général' },
];

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function extractError(err) {
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

export default function MediaLibraryPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copied, setCopied] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [filters, setFilters] = useState({ dossier: '', search: '', page: 1, limit: 18 });

  const handleClearFilters = () => {
    setFilters({ dossier: '', search: '', page: 1, limit: 10 });
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.dossier) params.dossier = filters.dossier;
      if (filters.search) params.search = filters.search;
      const { data: res } = await mediaService.list(params);
      setData(res.data);
      const paginationState = {
        total: 0,
        totalPages: 1,
        currentPage: filters.page,
      };
      if (res.pagination) {
        paginationState.total = res.pagination.total ?? 0;
        paginationState.totalPages = res.pagination.totalPages ?? 1;
        paginationState.currentPage = res.pagination.page ?? filters.page;
      }
      setPagination(paginationState);
    } catch (err) { showToast(extractError(err), 'danger'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    try {
      await mediaService.remove(deleteTarget.id);
      showToast('Fichier supprimé avec succès (S3 + base de données)');
      setDeleteTarget(null); fetchData();
    } catch (err) { showToast(extractError(err), 'danger'); setDeleteTarget(null); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>MÉDIATHÈQUE</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4 d-flex flex-wrap gap-3 align-items-end" style={{ border: '1px solid #eee' }}>
        <div style={{ flex: '1 1 160px' }}>
          <Form.Label className="small fw-semibold d-block mb-1">Dossier</Form.Label>
          <Form.Select size="sm" value={filters.dossier} onChange={e => setFilters(f => ({ ...f, dossier: e.target.value, page: 1 }))}>
            <option value="">Tous les dossiers</option>
            {FOLDER_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </Form.Select>
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <Form.Label className="small fw-semibold d-block mb-1">Recherche</Form.Label>
          <Form.Control size="sm" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} placeholder="Nom du fichier..." />
        </div>
        {['dossier', 'search'].some((k) => filters[k]) && (
          <Button size="sm" variant="outline-secondary" onClick={handleClearFilters} className="text-uppercase px-2">
            EFFACER
          </Button>
        )}
        {/* <Button size="sm" onClick={fetchData} style={{ backgroundColor: '#8B0000', border: 'none' }} className="text-uppercase px-2">RECHERCHER</Button> */}
        <Button size="sm" style={{ backgroundColor: 'transparent', border: 'none' }} className=" px-2">

          <span className=" text-secondary small">{pagination.total} fichier{pagination.total !== 1 ? 's' : ''}</span>
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8B0000' }} /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-5 text-secondary">
          <BsImage size={48} style={{ opacity: 0.3 }} />
          <p className="mt-3">Aucun fichier trouvé.<br /><small>Les fichiers apparaissent ici automatiquement après chaque upload.</small></p>
        </div>
      ) : (
        <>
          <Row className="g-3">
            {data.map(item => (
              <Col xs={6} sm={4} md={3} lg={2} key={item.id}>
                <div className="bg-white rounded" style={{ border: '1px solid #eee', overflow: 'hidden' }}>
                  {/* Preview */}
                  <div style={{ height: 110, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {item.file_type === 'image' ? (
                      <img
                        src={item.s3_url}
                        alt={item.nom}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            "https://placehold.co/300x300?text=No+Image";
                        }}
                      />
                    ) : (
                      <BsImage size={36} color="#ccc" />
                    )}
                  </div>
                  {/* Info */}
                  <div style={{ padding: '6px 8px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.nom}>{item.nom}</div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{formatBytes(item.taille_bytes)}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{formatDate(item.created_at)}</div>
                    <div style={{ fontSize: 10, color: '#8B0000', marginTop: 2 }}>{item.dossier}</div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
                    <button onClick={() => copyUrl(item.s3_url)}
                      style={{ flex: 1, border: 'none', background: 'none', padding: '5px 0', cursor: 'pointer', fontSize: 11, color: copied === item.s3_url ? '#155724' : '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      <BsClipboard size={12} /> {copied === item.s3_url ? 'Copié!' : 'URL'}
                    </button>
                    {isViewer ?
                      <></>
                      :
                      <button onClick={() => setDeleteTarget(item)}
                        style={{ flex: 1, border: 'none', borderLeft: '1px solid #f0f0f0', background: 'none', padding: '5px 0', cursor: 'pointer', fontSize: 11, color: '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <BsTrash size={12} /> Suppr.
                      </button>
                    }
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Pagination
            currentPage={filters.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          {/* <button onClick={() => setDeleteTarget(null)} className="modal-close-btn">×</button> */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer ce fichier ?</h6>
            <p style={{ fontSize: 13, color: '#e65100', margin: 0, fontWeight: 600 }}>⚠ Ce fichier sera supprimé définitivement d'AWS S3.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {deleteTarget.type_mime?.startsWith('image/') && (
                  <img src={deleteTarget.s3_url} alt="preview" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                )}
                <div>
                  <div className="fw-semibold" style={{ wordBreak: 'break-all' }}>{deleteTarget.nom}</div>
                  <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>{formatBytes(deleteTarget.taille_bytes)} · {deleteTarget.dossier}</div>
                  <div style={{ color: '#888', fontSize: 12 }}>{formatDate(deleteTarget.created_at)}</div>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setDeleteTarget(null)} style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>ANNULER</button>
            <button onClick={handleDelete} style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}>SUPPRIMER</button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
